/**
 * Cache LRU (Least Recently Used) pour pages PDF en mémoire vive.
 * Empêche les fuites de mémoire et les saturations RAM lors du défilement
 * de documents volumineux (ex: 1000 pages).
 */
export class LruPageCache {
  private cache = new Map<number, Uint8Array>();
  private readonly maxCapacity: number;

  constructor(maxCapacity = 20) {
    this.maxCapacity = Math.max(5, maxCapacity);
  }

  public get(pageNumber: number): Uint8Array | undefined {
    const item = this.cache.get(pageNumber);
    if (!item) return undefined;

    // Si le buffer a été détaché par un Web Worker, l'évincer immédiatement
    if (item.byteLength === 0) {
      this.cache.delete(pageNumber);
      return undefined;
    }

    // Réordonner : supprimer et réinsérer à la fin pour marquer comme récemment utilisé
    this.cache.delete(pageNumber);
    this.cache.set(pageNumber, item);
    return item;
  }

  public set(pageNumber: number, data: Uint8Array): void {
    if (!data || data.byteLength === 0) return;

    if (this.cache.has(pageNumber)) {
      this.cache.delete(pageNumber);
    } else if (this.cache.size >= this.maxCapacity) {
      // Évincer la page la moins récemment utilisée (première clé insérée)
      const oldestKey = this.cache.keys().next().value;
      if (oldestKey !== undefined) {
        this.cache.delete(oldestKey);
      }
    }

    this.cache.set(pageNumber, data);
  }

  public has(pageNumber: number): boolean {
    const item = this.cache.get(pageNumber);
    if (!item || item.byteLength === 0) {
      if (item) this.cache.delete(pageNumber);
      return false;
    }
    return true;
  }

  public delete(pageNumber: number): boolean {
    return this.cache.delete(pageNumber);
  }

  public clear(): void {
    this.cache.clear();
  }

  public get size(): number {
    return this.cache.size;
  }
}
